import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageHeader, StatusBadge, EmptyState } from '@/components/Shared';
import { money, shortDate } from '@/lib/format';
import { FileText, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const METHODS = ['Cash', 'Check', 'Zelle', 'Venmo', 'Bank Transfer', 'Other'];
let ITEM_SEQ = 0;
const newItem = () => ({ _uid: `item-${++ITEM_SEQ}`, product_id: '', quantity: 1 });

export default function Invoices() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: '', payment_method: 'Cash', notes: '', discount_percent: '', commission_percent: '', commission_amount: '', items: [newItem()] });

  const load = useCallback(async () => {
    const { data } = await api.get('/invoices', { params: { q: q || undefined, status: status === 'all' ? undefined : status } });
    setInvoices(data);
  }, [q, status]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { if (isAdmin) { api.get('/customers').then(({ data }) => setCustomers(data)); api.get('/products').then(({ data }) => setProducts(data)); } }, [isAdmin]);

  const selectedCustomer = customers.find((c) => c.id === form.customer_id);
  const onSelectCustomer = (v) => {
    const c = customers.find((x) => x.id === v);
    setForm({ ...form, customer_id: v, discount_percent: c ? String(c.discount_percent || 0) : '' });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, newItem()] });
  const setItem = (i, key, val) => { const items = [...form.items]; items[i][key] = key === 'quantity' ? Number(val) : val; setForm({ ...form, items }); };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const create = async () => {
    if (!form.customer_id) { toast.error('Select a customer'); return; }
    if (form.items.some((it) => !it.product_id)) { toast.error('Select products for all lines'); return; }
    try {
      const payload = {
        ...form,
        discount_percent: form.discount_percent === '' ? undefined : Math.max(0, Math.min(50, Number(form.discount_percent))),
        commission_amount: form.commission_amount === '' ? undefined : Math.max(0, Number(form.commission_amount)),
        commission_percent: form.commission_amount !== '' ? undefined : (form.commission_percent === '' ? undefined : Math.max(0, Math.min(100, Number(form.commission_percent)))),
      };
      await api.post('/invoices', payload);
      toast.success('Invoice created & inventory updated'); setOpen(false);
      setForm({ customer_id: '', payment_method: 'Cash', notes: '', discount_percent: '', commission_percent: '', commission_amount: '', items: [newItem()] }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const estSubtotal = form.items.reduce((s, it) => { const p = products.find((x) => x.id === it.product_id); return s + (p ? p.sale_price * it.quantity : 0); }, 0);
  const estPct = form.discount_percent === '' ? 0 : Math.max(0, Math.min(50, Number(form.discount_percent) || 0));
  const estDiscount = estSubtotal * estPct / 100;
  const estTotal = estSubtotal - estDiscount;
  const estCommission = form.commission_amount !== '' ? Math.max(0, Number(form.commission_amount) || 0)
    : (form.commission_percent === '' ? 0 : estTotal * Math.max(0, Math.min(100, Number(form.commission_percent) || 0)) / 100);

  return (
    <div>
      <PageHeader title={isAdmin ? 'Invoices' : 'My Invoices'} subtitle="Professional invoices with PDF export" testid="invoices-header"
        actions={isAdmin && <Button onClick={() => setOpen(true)} data-testid="invoices-add-button"><Plus className="h-4 w-4 mr-2" /> New Invoice</Button>} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice #, customer, employee…" className="pl-9" data-testid="invoices-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[180px]" data-testid="invoices-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem><SelectItem value="Unpaid">Unpaid</SelectItem><SelectItem value="Partially Paid">Partially Paid</SelectItem><SelectItem value="Paid">Paid</SelectItem></SelectContent>
        </Select>
      </div>

      <Card className="p-0 overflow-hidden">
        {invoices.length === 0 ? <div className="p-6"><EmptyState icon={FileText} title="No invoices" description="Invoices from sales and approved requests appear here." /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Invoice #</TableHead><TableHead>Date</TableHead><TableHead>Customer</TableHead>{isAdmin && <TableHead>Employee</TableHead>}
                <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead>Method</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {invoices.map((i) => (
                  <TableRow key={i.id} className="cursor-pointer" data-testid="invoice-row" onClick={() => navigate(`/invoices/${i.id}`)}>
                    <TableCell className="font-medium">{i.invoice_number}</TableCell>
                    <TableCell>{shortDate(i.created_at)}</TableCell>
                    <TableCell>{i.customer_name}</TableCell>
                    {isAdmin && <TableCell>{i.employee_name || '-'}</TableCell>}
                    <TableCell className="text-right tabular-nums font-semibold">{money(i.total)}</TableCell>
                    <TableCell><StatusBadge kind="payment" value={i.payment_status} /></TableCell>
                    <TableCell>{i.payment_method}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">New Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={onSelectCustomer}>
                  <SelectTrigger className="mt-1.5" data-testid="invoice-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.type === 'employee' ? ' (Employee)' : ''} · {c.discount_percent || 0}%</SelectItem>)}</SelectContent>
                </Select>
                {selectedCustomer && <p className="text-xs text-muted-foreground mt-1">Default discount: {selectedCustomer.discount_percent || 0}%{selectedCustomer.type === 'employee' ? ' (employee)' : ''}</p>}
              </div>
              <div><Label>Payment Method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Discount % (override, 0–50)</Label><Input type="number" min="0" max="50" step="0.5" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} placeholder="0" data-testid="invoice-discount-input" className="mt-1.5" /></div>
              <div><Label>Commission % (manual, 0–100)</Label><Input type="number" min="0" max="100" step="0.5" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} placeholder="0" disabled={form.commission_amount !== ''} data-testid="invoice-commission-percent-input" className="mt-1.5" /></div>
              <div><Label>Commission $ (overrides %)</Label><Input type="number" min="0" step="0.01" value={form.commission_amount} onChange={(e) => setForm({ ...form, commission_amount: e.target.value })} placeholder="0.00" data-testid="invoice-commission-amount-input" className="mt-1.5" /></div>
            </div>
            <div className="space-y-2">
              <Label>Line Items</Label>
              {form.items.map((it, i) => (
                <div key={it._uid} className="flex gap-2 items-center">
                  <Select value={it.product_id} onValueChange={(v) => setItem(i, 'product_id', v)}>
                    <SelectTrigger className="flex-1" data-testid="invoice-item-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.quantity} in stock)</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" min="1" value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} className="w-20" data-testid="invoice-item-qty-input" />
                  {form.items.length > 1 && <Button variant="ghost" size="icon" onClick={() => removeItem(i)}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addItem} data-testid="invoice-add-item-button"><Plus className="h-4 w-4 mr-1" /> Add Item</Button>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
            <div className="rounded-lg border bg-[hsl(var(--secondary))] p-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Original Subtotal</span><span className="tabular-nums">{money(estSubtotal)}</span></div>
              <div className="flex justify-between text-amber-700"><span>Discount ({estPct}%)</span><span className="tabular-nums">- {money(estDiscount)}</span></div>
              <div className="flex justify-between font-semibold border-t pt-1"><span>Final Total</span><span className="tabular-nums" data-testid="invoice-estimate-total">{money(estTotal)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Commission</span><span className="tabular-nums" data-testid="invoice-estimate-commission">{money(estCommission)}</span></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="invoice-create-button">Create Invoice</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
